<script setup lang="ts">
import { computed } from 'vue'
import { useDeckPlayerContext } from '../../composables/useDeckPlayerShell'
import { useFloatingPanel } from '../../composables/useFloatingPanel'

/**
 * Floating engine inspector for testing (localStorage 'perlingo.debug'): cursor,
 * registers, stats, call stack, "Ask: known?" answers, phase scores and flow signals,
 * plus pause/step/resume and the step delay.
 */
const player = useDeckPlayerContext()
const { showDebugPanel, closeDebugPanel, runner } = player
const {
  cursorNodeId: graphCursorNodeId,
  activePhaseId: graphActivePhaseId,
  activeGroupId: graphActiveGroupId,
  currentCardId: graphCurrentCardId,
  isInitializing: graphIsInitializing,
  registers: graphRegisters,
  callStack: graphCallStack,
  needsUserInteraction: graphNeedsUserInteraction,
  manualContinuation: graphManualContinuation,
  phaseInterlude: graphPhaseInterlude,
  awaitingAnswer: graphAwaitingAnswer,
  cardAnswers: graphCardAnswers,
  flowSignals: graphFlowSignals,
} = player.state
const { dbgPaused, dbgStepDelayMs, debugState } = runner

// Answers of this run and the phase scores the flow's getters see
const answerList = computed(() => [...graphCardAnswers.value.entries()])
const sessionKnown = computed(() => answerList.value.filter(([, known]) => known).length)
const currentPhaseId = computed(() => player.props.flowContext?.currentPhaseId ?? null)
/** Stored answers of the other levels, plus this run's for the level's own phase. */
const phaseScores = computed(() => {
  const stats = player.props.flowContext?.phaseStats ?? {}
  return Object.entries(stats).map(([id, s]) => {
    const live = id === currentPhaseId.value
    const known = s.known + (live ? sessionKnown.value : 0)
    const answered = s.answered + (live ? answerList.value.length : 0)
    return {
      id,
      live,
      known,
      answered,
      pct: answered ? Math.round((known / answered) * 100) : null,
    }
  })
})
const shortCardId = (id: string) => id.replace(/^card-/, '#')

// A fast double click on Pause/Resume/Step could fire a cascading continuation more
// than once; all controls share one 220 ms debounce.
let lastControlAt = 0
const debounced = (fn: () => void) => () => {
  const now = Date.now()
  if (now - lastControlAt < 220) return
  lastControlAt = now
  fn()
}
const dbgPauseClick = debounced(runner.dbgPause)
const dbgResumeClick = debounced(runner.dbgResume)
const dbgStepClick = debounced(runner.dbgStep)

// Default position: top-right, below the WordPress admin bar.
const {
  panelRef: dbgPanelRef,
  style: dbgPanelStyle,
  startDrag: startDbgDrag,
  startResize: startDbgResize,
} = useFloatingPanel({
  position: { left: window.innerWidth - 300, top: 40 },
  // Tall enough for the answer and phase-score sections without scrolling.
  size: { width: 280, height: Math.min(640, window.innerHeight - 60) },
  minSize: { width: 200, height: 150 },
})
</script>

<template>
  <Teleport to="body">
    <div v-if="showDebugPanel" ref="dbgPanelRef" class="pfc-dbg-float" :style="dbgPanelStyle">
      <div class="pfc-dbg-header" @mousedown="startDbgDrag">
        <span class="pfc-dbg-title">Perlingo Debug</span>
        <div class="pfc-dbg-header-actions" @mousedown.stop>
          <!-- Pause / Resume -->
          <button
            v-if="!dbgPaused"
            class="pfc-dbg-ctrl pfc-dbg-ctrl--pause"
            type="button"
            title="Pause execution"
            @click="dbgPauseClick"
          >
            ⏸
          </button>
          <button
            v-else
            class="pfc-dbg-ctrl pfc-dbg-ctrl--resume"
            type="button"
            title="Resume"
            @click="dbgResumeClick"
          >
            ▶
          </button>
          <!-- Step (only when paused) -->
          <button
            v-if="dbgPaused"
            class="pfc-dbg-ctrl pfc-dbg-ctrl--step"
            type="button"
            title="Step forward"
            :disabled="graphNeedsUserInteraction || !graphManualContinuation"
            @click="dbgStepClick"
          >
            ⏭
          </button>
          <button class="pfc-dbg-close" type="button" @click="closeDebugPanel">✕</button>
        </div>
      </div>

      <!-- Speed slider (always visible) -->
      <div class="pfc-dbg-speed-row">
        <span class="pfc-dbg-k">Speed</span>
        <input
          type="range"
          class="pfc-dbg-speed-slider"
          min="25"
          max="1500"
          step="25"
          :value="dbgStepDelayMs"
          @input="dbgStepDelayMs = Number(($event.target as HTMLInputElement).value)"
        />
        <span class="pfc-dbg-k pfc-dbg-speed-val">{{ `${dbgStepDelayMs}ms` }}</span>
      </div>

      <div class="pfc-dbg-scroll">
        <div class="pfc-dbg-section">
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Node</span
            ><span class="pfc-dbg-v">{{ graphCursorNodeId ?? '—' }}</span>
          </div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Phase</span
            ><span class="pfc-dbg-v">{{ graphActivePhaseId ?? '—' }}</span>
          </div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Group</span
            ><span class="pfc-dbg-v">{{ graphActiveGroupId ?? '—' }}</span>
          </div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Card</span
            ><span class="pfc-dbg-v">{{ graphCurrentCardId ?? '—' }}</span>
          </div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Initializing</span
            ><span class="pfc-dbg-v">{{ graphIsInitializing }}</span>
          </div>
        </div>

        <div class="pfc-dbg-section">
          <div class="pfc-dbg-label">Registers</div>
          <div class="pfc-dbg-registers">
            <div v-for="(val, key) in graphRegisters" :key="key" class="pfc-dbg-reg">
              <span class="pfc-dbg-k">{{ key }}</span>
              <span class="pfc-dbg-v pfc-dbg-v--reg">{{ val }}</span>
            </div>
          </div>
        </div>

        <div class="pfc-dbg-section">
          <div class="pfc-dbg-label">Stats</div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Deck done</span
            ><span class="pfc-dbg-v">{{ debugState.deckDone }} / {{ debugState.deckSize }}</span>
          </div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Group done</span
            ><span class="pfc-dbg-v">{{ debugState.groupDone }} / {{ debugState.groupSize }}</span>
          </div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Pool left</span
            ><span class="pfc-dbg-v">{{ debugState.groupPoolRemaining }}</span>
          </div>
          <div v-if="debugState.progressTotal !== null" class="pfc-dbg-row">
            <span class="pfc-dbg-k">Progress</span
            ><span class="pfc-dbg-v"
              >{{ debugState.progressCurrent }} / {{ debugState.progressTotal }}</span
            >
          </div>
        </div>

        <div class="pfc-dbg-section">
          <div class="pfc-dbg-label">Answers (Ask: known?)</div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Waiting</span
            ><span class="pfc-dbg-v">{{
              graphAwaitingAnswer ? shortCardId(graphAwaitingAnswer.cardId) : '—'
            }}</span>
          </div>
          <div v-if="graphAwaitingAnswer" class="pfc-dbg-answer-btns">
            <button type="button" class="pfc-dbg-ans" @click="runner.answerCard(false)">
              ✗ unknown
            </button>
            <button type="button" class="pfc-dbg-ans" @click="runner.answerCard(true)">
              ✓ known
            </button>
          </div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">This level</span
            ><span class="pfc-dbg-v">{{ sessionKnown }} / {{ answerList.length }} known</span>
          </div>
          <div v-if="answerList.length" class="pfc-dbg-answers">
            <span
              v-for="[cardId, known] in answerList"
              :key="cardId"
              class="pfc-dbg-chip"
              :class="known ? 'pfc-dbg-chip--known' : 'pfc-dbg-chip--unknown'"
              >{{ known ? '✓' : '✗' }}{{ shortCardId(cardId) }}</span
            >
          </div>
        </div>

        <div class="pfc-dbg-section">
          <div class="pfc-dbg-label">Phase scores</div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Level phase</span
            ><span class="pfc-dbg-v">{{ currentPhaseId ?? '— (none / practice)' }}</span>
          </div>
          <div v-if="!phaseScores.length" class="pfc-dbg-row">
            <span class="pfc-dbg-k">no course data</span>
          </div>
          <div v-for="p in phaseScores" :key="p.id" class="pfc-dbg-row">
            <span class="pfc-dbg-k">{{ p.id }}{{ p.live ? ' (live)' : '' }}</span
            ><span class="pfc-dbg-v"
              >{{ p.known }} / {{ p.answered }}{{ p.pct !== null ? ` · ${p.pct}%` : '' }}</span
            >
          </div>
        </div>

        <div class="pfc-dbg-section">
          <div class="pfc-dbg-label">Signals</div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-v">{{
              graphFlowSignals.length ? graphFlowSignals.join(', ') : '—'
            }}</span>
          </div>
        </div>

        <div v-if="graphCallStack.length" class="pfc-dbg-section">
          <div class="pfc-dbg-label">Call Stack</div>
          <div v-for="(frame, i) in graphCallStack" :key="i" class="pfc-dbg-row">
            <span class="pfc-dbg-k">#{{ i }}</span>
            <span class="pfc-dbg-v">{{ frame.callNodeId }} ({{ frame.entryPort }})</span>
          </div>
        </div>

        <div class="pfc-dbg-section pfc-dbg-section--last">
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Initializing</span
            ><span class="pfc-dbg-v">{{ graphIsInitializing }}</span>
          </div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Needs input</span
            ><span class="pfc-dbg-v">{{ graphNeedsUserInteraction }}</span>
          </div>
          <div class="pfc-dbg-row">
            <span class="pfc-dbg-k">Phase interlude</span
            ><span class="pfc-dbg-v">{{ graphPhaseInterlude }}</span>
          </div>
        </div>
      </div>

      <div class="pfc-panel-resize-handle" @mousedown="startDbgResize" />
    </div>
  </Teleport>
</template>

<style scoped src="./styles/floatingPanel.css"></style>

<style scoped>
/* ── Floating debug panel ────────────────────────────────────────────────── */
.pfc-dbg-float {
  position: fixed;
  z-index: 999999;
  background: #0f172a;
  color: #94a3b8;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Courier New', monospace;
  font-size: 0.72rem;
  line-height: 1.45;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  min-height: 150px;
}

.pfc-dbg-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0.65rem;
  background: #1e293b;
  cursor: grab;
  user-select: none;
  border-bottom: 1px solid #334155;
  flex-shrink: 0;
  gap: 0.4rem;
}

.pfc-dbg-header:active {
  cursor: grabbing;
}

.pfc-dbg-title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #f8fafc;
  flex: 1;
}

.pfc-dbg-header-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pfc-dbg-ctrl {
  all: unset;
  cursor: pointer;
  font-size: 0.78rem;
  line-height: 1;
  padding: 0.1rem 0.25rem;
  border-radius: 3px;
  transition: background 0.1s;
}

.pfc-dbg-ctrl:disabled {
  opacity: 0.35;
  cursor: default;
}
.pfc-dbg-ctrl--pause {
  color: #fbbf24;
}
.pfc-dbg-ctrl--pause:not(:disabled):hover {
  background: #292524;
}
.pfc-dbg-ctrl--resume {
  color: #4ade80;
}
.pfc-dbg-ctrl--resume:not(:disabled):hover {
  background: #14532d33;
}
.pfc-dbg-ctrl--step {
  color: #38bdf8;
}
.pfc-dbg-ctrl--step:not(:disabled):hover {
  background: #0c4a6e33;
}

.pfc-dbg-close {
  all: unset;
  cursor: pointer;
  color: #64748b;
  font-size: 0.75rem;
  line-height: 1;
  padding: 0.1rem 0.2rem;
  border-radius: 3px;
}
.pfc-dbg-close:hover {
  color: #f8fafc;
  background: #334155;
}

/* Speed row */
.pfc-dbg-speed-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.65rem;
  background: #0f172a;
  border-bottom: 1px solid #1e293b;
  flex-shrink: 0;
}

.pfc-dbg-speed-slider {
  flex: 1;
  height: 3px;
  accent-color: #38bdf8;
  cursor: pointer;
}

.pfc-dbg-speed-val {
  min-width: 2.5rem;
  text-align: right;
}

/* Scrollable body */
.pfc-dbg-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.pfc-dbg-section {
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid #1e293b;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.pfc-dbg-section--last {
  border-bottom: none;
}

.pfc-dbg-label {
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #475569;
  margin-bottom: 0.15rem;
}

.pfc-dbg-row {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.pfc-dbg-k {
  color: #64748b;
  flex-shrink: 0;
}
.pfc-dbg-v {
  color: #e2e8f0;
  text-align: right;
  word-break: break-all;
}

.pfc-dbg-registers {
  display: flex;
  gap: 1rem;
}

.pfc-dbg-reg {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
}

.pfc-dbg-reg .pfc-dbg-k {
  font-size: 0.62rem;
}
.pfc-dbg-v--reg {
  color: #38bdf8;
  font-weight: 700;
  font-size: 0.9rem;
}

.pfc-dbg-answer-btns {
  display: flex;
  gap: 0.3rem;
  margin: 0.2rem 0;
}
.pfc-dbg-ans {
  flex: 1;
  padding: 0.15rem 0.3rem;
  border: 1px solid #334155;
  border-radius: 4px;
  background: #1e293b;
  color: #e2e8f0;
  font: inherit;
  cursor: pointer;
}
.pfc-dbg-ans:hover {
  background: #334155;
}
.pfc-dbg-answers {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
  margin-top: 0.2rem;
}
.pfc-dbg-chip {
  padding: 0 0.3rem;
  border-radius: 3px;
  font-size: 0.66rem;
}
.pfc-dbg-chip--known {
  background: rgba(22, 163, 74, 0.25);
  color: #86efac;
}
.pfc-dbg-chip--unknown {
  background: rgba(220, 38, 38, 0.25);
  color: #fca5a5;
}
</style>
